import './index.css';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useCallback, useEffect } from 'react';
import { DataProviderError } from '@hh.ru/magritte-common-data-provider';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { getSearchResultCount, SuggestPicker } from './SuggestPicker.js';
import { useSuggestKeyboardNav } from './useSuggestKeyboardNav.js';
import { s as styles } from './sectionRenderers-BGy3XMzM.js';
import 'classnames';
import '@hh.ru/magritte-common-keyboard';
import '@hh.ru/magritte-ui-bottom-sheet';
import '@hh.ru/magritte-ui-button';
import '@hh.ru/magritte-ui-drop';
import '@hh.ru/magritte-ui-icon/icon';
import '@hh.ru/magritte-ui-navigation-bar';
import '@hh.ru/magritte-ui-card';
import '@hh.ru/magritte-ui-cell';
import '@hh.ru/magritte-ui-chips';
import '@hh.ru/magritte-ui-typography';

const SuggestTriggerDefault = (props) => {
    const { component: TriggerComponent, props: triggerProps } = props;
    return (jsx(TriggerComponent, { ...triggerProps }));
};
const SuggestComponent = function ({ dataProvider, input, inputValue, onSelectValidator = () => true, navigationBarProps, maxHeight = 460, itemsGap = 4, onBlur, topContent, bottomContent, dropWidth, errorPlaceholder, trls, onChange, onDropOpen, onDropClose, onBottomSheetOpen, onBottomSheetClose, onSearchSubmit, renderTrigger = SuggestTriggerDefault, renderItem, }, ref) {
    const { component: InputComponent, props: inputProps } = input;
    const [inputValueState, setInputValueState] = useState(inputProps.value || '');
    const { onChange: onInputChange, onFocus: onInputFocus, onKeyDown: onInputKeyDown } = inputProps;
    const [requestResult, setRequestResult] = useState(null);
    const [valueSelectedFromSuggest, setValueSelectedFromSuggest] = useState(false);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const suggestContainerRef = useRef(null);
    const inputContainerRef = useRef(null);
    const inputRefMulti = useMultipleRefs(inputRef, ref);
    const onBlurRef = useRef(onBlur);
    onBlurRef.current = onBlur; // update actual callback
    const { isMobile } = useBreakpoint();
    const [isError, setIsError] = useState(false);
    const currentRequestRef = useRef();
    const actualInputValue = inputValue === undefined ? inputValueState : inputValue;
    const { focused, onKeyDown, onFocus, ...rest } = useSuggestKeyboardNav(suggestContainerRef, inputContainerRef, setValueSelectedFromSuggest, actualInputValue, inputRef, onBlurRef);
    const handleBottomSheetClose = () => {
        rest.onBottomSheetClose();
        onBottomSheetClose?.();
    };
    const onChangeHandler = useCallback((value, skipSuggestChangeHandler = false) => {
        if (inputValue === undefined) {
            setInputValueState(value);
        }
        !skipSuggestChangeHandler && onChange?.(value, false);
        onInputChange?.(value);
    }, [inputValue, setInputValueState, onInputChange, onChange]);
    const requestData = useCallback((query) => {
        setIsError(false);
        currentRequestRef.current?.cancel();
        const request = dataProvider(query);
        request.then((result) => {
            setRequestResult({ query, result });
        }, (event) => {
            // включаем error state если это НЕ ошибка, вызванная `minCharsCount` или отменненым запросом
            if (!(event instanceof DataProviderError)) {
                setIsError(true);
            }
            setRequestResult(null);
        });
        currentRequestRef.current = request;
    }, [dataProvider]);
    useEffect(() => {
        if (valueSelectedFromSuggest !== false && requestResult?.query !== actualInputValue) {
            setRequestResult(null);
        }
        if (valueSelectedFromSuggest !== actualInputValue.trim()) {
            setValueSelectedFromSuggest(false);
            if (focused) {
                requestData(actualInputValue);
            }
        }
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [actualInputValue, requestData, focused]);
    const onFocusHandler = useCallback((event) => {
        setValueSelectedFromSuggest(false);
        if (actualInputValue !== requestResult?.query) {
            setRequestResult(null);
            requestData(actualInputValue);
        }
        onInputFocus?.(event);
        onFocus(event);
    }, [actualInputValue, requestResult?.query, onInputFocus, onFocus, requestData]);
    const onValueSelectHandler = useCallback((value, data) => {
        if (!onSelectValidator(value, data)) {
            return;
        }
        if (!isMobile) {
            inputRef.current?.focus();
        }
        onChangeHandler(value, true);
        onChange?.(value, true, data);
        setValueSelectedFromSuggest(value);
    }, [onSelectValidator, isMobile, onChangeHandler, onChange]);
    const onKeyDownHandler = useCallback((event) => {
        onKeyDown(event);
        onInputKeyDown?.(event);
    }, [onInputKeyDown, onKeyDown]);
    return (jsxs(Fragment, { children: [jsx("div", { ref: inputContainerRef, className: styles.suggestInputContainer, children: renderTrigger({
                    component: InputComponent,
                    props: {
                        ...inputProps,
                        value: actualInputValue,
                        onFocus: onFocusHandler,
                        onChange: onChangeHandler,
                        onKeyDown: onKeyDownHandler,
                        ref: inputRefMulti,
                        wrapperRef,
                        autoComplete: 'off',
                    },
                    optionsCount: getSearchResultCount(requestResult?.result),
                }) }), jsx(SuggestPicker, { data: requestResult?.result, activatorRef: wrapperRef, inputRef: inputRef, inputFocused: focused, currentInputValueSelectedFromSuggest: valueSelectedFromSuggest !== false, onValueSelect: onValueSelectHandler, maxHeight: maxHeight, navigationBarProps: navigationBarProps, suggestContainerRef: suggestContainerRef, itemsGap: itemsGap, onBottomSheetClose: handleBottomSheetClose, onBottomSheetOpen: onBottomSheetOpen, onDropOpen: onDropOpen, onDropClose: onDropClose, inputValue: actualInputValue, topContent: topContent, bottomContent: bottomContent, dropWidth: dropWidth, isError: isError, trls: trls, errorPlaceholder: errorPlaceholder, renderItem: renderItem, onClickReload: () => {
                    requestData(actualInputValue);
                }, onSearchSubmit: onSearchSubmit, input: {
                    component: InputComponent,
                    props: {
                        ...input.props,
                        onFocus: onFocusHandler,
                        onChange: onChangeHandler,
                        value: actualInputValue,
                        onKeyDown: onKeyDownHandler,
                    },
                } })] }));
};
const Suggest = forwardRef(SuggestComponent);
Suggest.displayName = 'Suggest';

export { Suggest };
//# sourceMappingURL=Suggest.js.map
